import { MONTH_NAMES } from './date';
import { YEAR_PAGE_SIZE, isMonthOutOfBounds, isYearOutOfBounds, monthIndex } from './monthBounds';
import type { MonthBounds } from './monthBounds';
import type { CalendarMonth } from './types';
import { cellStateClass, type CellState } from './cellStateClass';

interface PickerCell extends CellState {
    key: number;
    label: string;
}

interface PickerGridProps {
    cells: PickerCell[];
    disabled?: boolean;
    onPick: (key: number) => void;
}

function PickerGrid({ cells, disabled, onPick }: PickerGridProps) {
    return (
        <div
            className={`grid h-full grid-cols-3 grid-rows-4 gap-2 text-center ${disabled ? 'opacity-40 pointer-events-none' : ''}`}
        >
            {cells.map((cell) => (
                <button
                    type="button"
                    key={cell.key}
                    onClick={() => onPick(cell.key)}
                    disabled={disabled || cell.outOfRange}
                    aria-pressed={cell.selected}
                    className={`h-full w-full text-sm rounded-lg transition-colors disabled:cursor-not-allowed ${cellStateClass(cell)}`}
                >
                    {cell.label}
                </button>
            ))}
        </div>
    );
}

interface GridProps {
    month: CalendarMonth;
    today: CalendarMonth;
    bounds: MonthBounds;
    disabled?: boolean;
}

interface MonthGridProps extends GridProps {
    onPickMonth: (month: number) => void;
}

export function MonthGrid({ month, today, bounds, disabled, onPickMonth }: MonthGridProps) {
    const cells = MONTH_NAMES.map((name, index) => ({
        key: index,
        label: name.slice(0, 3),
        selected: index === month.month,
        current: month.year === today.year && index === today.month,
        outOfRange: isMonthOutOfBounds(monthIndex({ year: month.year, month: index }), bounds)
    }));
    return <PickerGrid cells={cells} disabled={disabled} onPick={onPickMonth} />;
}

interface YearGridProps extends GridProps {
    pageStart: number;
    onPickYear: (year: number) => void;
}

export function YearGrid({ month, today, bounds, disabled, pageStart, onPickYear }: YearGridProps) {
    const cells = Array.from({ length: YEAR_PAGE_SIZE }, (_, index) => {
        const year = pageStart + index;
        return {
            key: year,
            label: String(year),
            selected: year === month.year,
            current: year === today.year,
            outOfRange: isYearOutOfBounds(year, bounds)
        };
    });
    return <PickerGrid cells={cells} disabled={disabled} onPick={onPickYear} />;
}
